import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';  // Assuming you have an AuthContext
import { useNavigate } from 'react-router-dom'; // For redirection in case of unauthorized access

interface RBACContextProps {
    isSuperAdmin: () => boolean;
    isDepartmentManager: (managers: User[] | null) => boolean;
    isTeamManager: (managers: User[] | null) => boolean;
    currentUserRoles: string[];
    isLoadingRoles: boolean; // To show a loading state if roles are being fetched
}

const RBACContext = createContext<RBACContextProps | undefined>(undefined);

// Custom hook to use the RBAC context
export const useRBAC = () => {
    const context = useContext(RBACContext);
    if (!context) {
        throw new Error('useRBAC must be used within a RBACProvider');
    }
    return context;
};

interface Department {
    id: number;
    name: string;
    teams: Team[];
    manager: User[] | null;
}

interface Team {
    id: number;
    name: string;
    members: User[];
    manager: User[] | null;
}

interface User {
    id: number;
    name: string;
}

export const RBACProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();  // Assuming `useAuth` gives you the current user details
    const [currentUserRoles, setCurrentUserRoles] = useState<string[]>([]);
    const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(true);
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchUserRoles = async () => {
            try {
                if (!user) return;
                
                const session = sessionStorage.getItem("tiller") || localStorage.getItem("tiller");
                const access_token = session ? JSON.parse(session).access_token : null;

                const response = await fetch(`${apiUrl}/user-roles/${user.id}`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${access_token}`,
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch user roles');
                }

                const data = await response.json();
                setCurrentUserRoles(data.roles || []);  // Assuming `data.roles` is an array of role names
            } catch (error) {
                console.error('Error fetching user roles:', error);
            } finally {
                setIsLoadingRoles(false);  // Set loading state to false after fetching
            }
        };

        fetchUserRoles();
    }, [user, apiUrl, navigate]);

    // Define RBAC utility functions
    const isSuperAdmin = () => currentUserRoles.includes('Super Admin');

    const isDepartmentManager = (managers: User[] | null): boolean => {
        // Ensure managers is not null and check if the current user is among the managers
        if (!managers) return false;
        return managers.some(manager => manager.id === Number(user?.id));
    };

    const isTeamManager = (managers: User[] | null): boolean => {
        // Ensure managers is not null and check if the current user is among the team managers
        if (!managers) return false;
        return managers.some(manager => manager.id === Number(user?.id));
    };

    return (
        <RBACContext.Provider value={{ isSuperAdmin, isDepartmentManager, isTeamManager, currentUserRoles, isLoadingRoles }}>
            {children}
        </RBACContext.Provider>
    );
};
